/**
 * CBO Role Types and Permissions System
 *
 * Defines role-based access control with a simplified 6-role system:
 * - super_admin: Platform-wide administration
 * - org_admin: Organization administration
 * - program_manager: Program administration and reporting
 * - supervisor: Team supervision and approvals
 * - staff: Standard member/enrollment access
 * - viewer: Read-only access
 *
 * Organization-specific role terminologies (like 'promotora', 'care_coordinator',
 * 'community_health_worker', etc.) are configured in role_terminology table
 * and map to one of these 6 system roles.
 *
 * This implements the RFP requirement for role-based experiences with
 * permission-aware fields/actions and least-privilege defaults.
 */

// ============================================================================
// SYSTEM ROLE TYPES (6 Core Roles)
// ============================================================================

/**
 * System Role Types - The 6 core roles that define permission levels
 *
 * Organization-specific terminologies (promotora, care_coordinator, CHW, navigator, etc.)
 * should be configured in role_terminology and map to one of these system roles.
 */
export type CBORoleType =
  | 'super_admin'      // Platform-wide administration (access level 6)
  | 'org_admin'        // Organization administration (access level 5)
  | 'program_manager'  // Program administration and reporting (access level 4)
  | 'supervisor'       // Team supervision and approvals (access level 3)
  | 'staff'            // Standard member/enrollment access (access level 2)
  | 'viewer';          // Read-only access (access level 1)

/**
 * System role access levels for hierarchy comparison
 */
export const SYSTEM_ROLE_ACCESS_LEVELS: Record<CBORoleType, number> = {
  super_admin: 6,
  org_admin: 5,
  program_manager: 4,
  supervisor: 3,
  staff: 2,
  viewer: 1,
};

/**
 * Legacy field worker role types
 * @deprecated These are now organization-specific terminologies in role_terminology table.
 * They map to 'staff' or 'supervisor' system roles.
 */
export type LegacyFieldWorkerRole = 'promotora' | 'care_coordinator';

/**
 * All role types including legacy for backward compatibility
 */
export type AllRoleTypes = CBORoleType | LegacyFieldWorkerRole;

/**
 * Maps legacy field worker roles to their system role
 */
export const LEGACY_ROLE_TO_SYSTEM_ROLE: Record<LegacyFieldWorkerRole, CBORoleType> = {
  promotora: 'staff',
  care_coordinator: 'staff',
};

/**
 * Check if a role string is a valid system role
 */
export function isSystemRole(role: string): role is CBORoleType {
  return ['super_admin', 'org_admin', 'program_manager', 'supervisor', 'staff', 'viewer'].includes(role);
}

/**
 * Convert any role (including legacy) to system role
 */
export function toSystemRole(role: string): CBORoleType {
  if (isSystemRole(role)) {
    return role;
  }
  // Check if it's a legacy role
  if (role === 'promotora' || role === 'care_coordinator') {
    return LEGACY_ROLE_TO_SYSTEM_ROLE[role];
  }
  // Default to staff for unknown roles
  return 'staff';
}

/**
 * Permission categories for CBO operations
 */
export type PermissionCategory =
  | 'members'
  | 'enrollments'
  | 'care_plans'
  | 'communications'
  | 'workflows'
  | 'reports'
  | 'audits'
  | 'settings'
  | 'team'
  | 'programs'
  | 'billing'
  | 'integrations';

/**
 * Permission actions
 */
export type PermissionAction = 'view' | 'create' | 'edit' | 'delete' | 'approve' | 'export';

/**
 * Full permission identifier
 */
export type Permission = `${PermissionCategory}:${PermissionAction}`;

// ============================================================================
// PERMISSION DEFINITIONS
// ============================================================================

/**
 * All available permissions in the system
 */
export const ALL_PERMISSIONS: Permission[] = [
  // Member permissions
  'members:view',
  'members:create',
  'members:edit',
  'members:delete',
  'members:export',

  // Enrollment permissions
  'enrollments:view',
  'enrollments:create',
  'enrollments:edit',
  'enrollments:delete',
  'enrollments:approve',
  'enrollments:export',

  // Care plan permissions
  'care_plans:view',
  'care_plans:create',
  'care_plans:edit',
  'care_plans:delete',
  'care_plans:approve',
  'care_plans:export',

  // Communication permissions
  'communications:view',
  'communications:create',
  'communications:edit',
  'communications:delete',
  'communications:export',

  // Workflow permissions
  'workflows:view',
  'workflows:create',
  'workflows:edit',
  'workflows:delete',
  'workflows:approve',
  'workflows:export',

  // Report permissions
  'reports:view',
  'reports:create',
  'reports:edit',
  'reports:delete',
  'reports:export',

  // Audit permissions
  'audits:view',
  'audits:create',
  'audits:edit',
  'audits:approve',
  'audits:export',

  // Settings permissions
  'settings:view',
  'settings:edit',

  // Team permissions
  'team:view',
  'team:create',
  'team:edit',
  'team:delete',

  // Program permissions
  'programs:view',
  'programs:create',
  'programs:edit',
  'programs:delete',
  'programs:approve',
  'programs:export',

  // Billing permissions
  'billing:view',
  'billing:create',
  'billing:edit',
  'billing:approve',
  'billing:export',

  // Integration permissions
  'integrations:view',
  'integrations:create',
  'integrations:edit',
  'integrations:delete',
];

// ============================================================================
// ROLE PERMISSION MAPPINGS
// ============================================================================

/**
 * Role metadata including display information and default permissions
 */
export interface RoleDefinition {
  type: CBORoleType;
  displayName: string;
  displayNameEs: string;
  description: string;
  descriptionEs: string;
  permissions: Permission[];
  defaultDashboard: string;
  canManageRoles: CBORoleType[];
  uiSimplificationLevel: 'full' | 'standard' | 'simplified';
  showAdvancedFeatures: boolean;
}

/**
 * Role definitions with permissions and metadata
 * Note: promotora and care_coordinator are now organization-specific terminologies
 * configured in role_terminology table, not system roles.
 */
export const ROLE_DEFINITIONS: Record<CBORoleType, RoleDefinition> = {
  super_admin: {
    type: 'super_admin',
    displayName: 'Super Administrator',
    displayNameEs: 'Super Administrador',
    description: 'Full platform access with all permissions',
    descriptionEs: 'Acceso completo a la plataforma con todos los permisos',
    permissions: ALL_PERMISSIONS,
    defaultDashboard: '/admin/dashboard',
    canManageRoles: ['super_admin', 'org_admin', 'program_manager', 'supervisor', 'staff', 'viewer'],
    uiSimplificationLevel: 'full',
    showAdvancedFeatures: true,
  },

  org_admin: {
    type: 'org_admin',
    displayName: 'Organization Administrator',
    displayNameEs: 'Administrador de Organización',
    description: 'Organization-wide administration and configuration',
    descriptionEs: 'Administración y configuración de toda la organización',
    permissions: ALL_PERMISSIONS.filter(p => !p.startsWith('integrations:')),
    defaultDashboard: '/admin/dashboard',
    canManageRoles: ['program_manager', 'supervisor', 'staff', 'viewer'],
    uiSimplificationLevel: 'full',
    showAdvancedFeatures: true,
  },

  program_manager: {
    type: 'program_manager',
    displayName: 'Program Manager',
    displayNameEs: 'Gerente de Programa',
    description: 'Manages programs, reviews reports, and oversees compliance',
    descriptionEs: 'Gestiona programas, revisa informes y supervisa el cumplimiento',
    permissions: [
      'members:view', 'members:create', 'members:edit', 'members:export',
      'enrollments:view', 'enrollments:create', 'enrollments:edit', 'enrollments:approve', 'enrollments:export',
      'care_plans:view', 'care_plans:create', 'care_plans:edit', 'care_plans:approve', 'care_plans:export',
      'communications:view', 'communications:create', 'communications:edit', 'communications:export',
      'workflows:view', 'workflows:create', 'workflows:edit', 'workflows:approve', 'workflows:export',
      'reports:view', 'reports:create', 'reports:edit', 'reports:export',
      'audits:view', 'audits:approve', 'audits:export',
      'settings:view',
      'team:view', 'team:edit',
      'programs:view', 'programs:create', 'programs:edit', 'programs:approve', 'programs:export',
      'billing:view', 'billing:export',
    ],
    defaultDashboard: '/program-manager/dashboard',
    canManageRoles: ['supervisor', 'staff'],
    uiSimplificationLevel: 'standard',
    showAdvancedFeatures: true,
  },

  supervisor: {
    type: 'supervisor',
    displayName: 'Supervisor',
    displayNameEs: 'Supervisor',
    description: 'Team supervision, case review, and compliance auditing',
    descriptionEs: 'Supervisión de equipo, revisión de casos y auditoría de cumplimiento',
    permissions: [
      'members:view', 'members:edit',
      'enrollments:view', 'enrollments:edit', 'enrollments:approve',
      'care_plans:view', 'care_plans:edit', 'care_plans:approve',
      'communications:view', 'communications:create', 'communications:edit',
      'workflows:view', 'workflows:edit', 'workflows:approve',
      'reports:view', 'reports:export',
      'audits:view', 'audits:create', 'audits:approve', 'audits:export',
      'team:view',
      'programs:view',
    ],
    defaultDashboard: '/supervisor/dashboard',
    canManageRoles: ['staff'],
    uiSimplificationLevel: 'standard',
    showAdvancedFeatures: false,
  },

  staff: {
    type: 'staff',
    displayName: 'Staff',
    displayNameEs: 'Personal',
    description: 'Standard member and enrollment access for field workers',
    descriptionEs: 'Acceso estándar a miembros e inscripciones para trabajadores de campo',
    permissions: [
      'members:view', 'members:create', 'members:edit',
      'enrollments:view', 'enrollments:create', 'enrollments:edit',
      'care_plans:view', 'care_plans:create', 'care_plans:edit',
      'communications:view', 'communications:create',
      'workflows:view',
      'reports:view',
    ],
    defaultDashboard: '/staff/dashboard',
    canManageRoles: [],
    uiSimplificationLevel: 'standard',
    showAdvancedFeatures: false,
  },

  viewer: {
    type: 'viewer',
    displayName: 'Viewer',
    displayNameEs: 'Observador',
    description: 'Read-only access to assigned areas',
    descriptionEs: 'Acceso de solo lectura a áreas asignadas',
    permissions: [
      'members:view',
      'enrollments:view',
      'care_plans:view',
      'reports:view',
    ],
    defaultDashboard: '/viewer/dashboard',
    canManageRoles: [],
    uiSimplificationLevel: 'simplified',
    showAdvancedFeatures: false,
  },
};

/**
 * Legacy role definitions for backward compatibility
 * @deprecated Use organization-specific role terminology instead
 */
export const LEGACY_ROLE_DEFINITIONS: Record<LegacyFieldWorkerRole, RoleDefinition> = {
  promotora: {
    type: 'staff', // Maps to staff
    displayName: 'Promotora',
    displayNameEs: 'Promotora',
    description: 'Community health worker focused on outreach and enrollment',
    descriptionEs: 'Trabajador de salud comunitaria enfocado en alcance e inscripción',
    permissions: ROLE_DEFINITIONS.staff.permissions,
    defaultDashboard: '/staff/dashboard',
    canManageRoles: [],
    uiSimplificationLevel: 'simplified',
    showAdvancedFeatures: false,
  },
  care_coordinator: {
    type: 'staff', // Maps to staff
    displayName: 'Care Coordinator',
    displayNameEs: 'Coordinador de Cuidado',
    description: 'Coordinates care plans and member services',
    descriptionEs: 'Coordina planes de cuidado y servicios para miembros',
    permissions: ROLE_DEFINITIONS.staff.permissions,
    defaultDashboard: '/staff/dashboard',
    canManageRoles: [],
    uiSimplificationLevel: 'standard',
    showAdvancedFeatures: false,
  },
};

// ============================================================================
// NAVIGATION CONFIGURATION BY ROLE
// ============================================================================

export interface NavItem {
  id: string;
  label: string;
  labelEs: string;
  icon: string;
  path: string;
  requiredPermissions: Permission[];
  children?: NavItem[];
  badge?: 'tasks' | 'alerts' | 'messages';
}

/**
 * Navigation items filtered by role permissions
 */
export const NAVIGATION_ITEMS: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    labelEs: 'Panel Principal',
    icon: 'LayoutDashboard',
    path: '/dashboard',
    requiredPermissions: [],
  },
  {
    id: 'my-tasks',
    label: 'My Tasks',
    labelEs: 'Mis Tareas',
    icon: 'CheckSquare',
    path: '/tasks',
    requiredPermissions: [],
    badge: 'tasks',
  },
  {
    id: 'members',
    label: 'Members',
    labelEs: 'Miembros',
    icon: 'Users',
    path: '/members',
    requiredPermissions: ['members:view'],
    children: [
      {
        id: 'member-list',
        label: 'All Members',
        labelEs: 'Todos los Miembros',
        icon: 'List',
        path: '/members/list',
        requiredPermissions: ['members:view'],
      },
      {
        id: 'member-add',
        label: 'Add Member',
        labelEs: 'Agregar Miembro',
        icon: 'UserPlus',
        path: '/members/add',
        requiredPermissions: ['members:create'],
      },
    ],
  },
  {
    id: 'enrollments',
    label: 'Enrollments',
    labelEs: 'Inscripciones',
    icon: 'ClipboardList',
    path: '/enrollments',
    requiredPermissions: ['enrollments:view'],
    children: [
      {
        id: 'enrollment-queue',
        label: 'Enrollment Queue',
        labelEs: 'Cola de Inscripciones',
        icon: 'ListOrdered',
        path: '/enrollments/queue',
        requiredPermissions: ['enrollments:view'],
        badge: 'tasks',
      },
      {
        id: 'enrollment-new',
        label: 'New Enrollment',
        labelEs: 'Nueva Inscripción',
        icon: 'FilePlus',
        path: '/enrollments/new',
        requiredPermissions: ['enrollments:create'],
      },
      {
        id: 'enrollment-pending',
        label: 'Pending Approval',
        labelEs: 'Pendientes de Aprobación',
        icon: 'Clock',
        path: '/enrollments/pending',
        requiredPermissions: ['enrollments:approve'],
        badge: 'alerts',
      },
    ],
  },
  {
    id: 'care-plans',
    label: 'Care Plans',
    labelEs: 'Planes de Cuidado',
    icon: 'Heart',
    path: '/care-plans',
    requiredPermissions: ['care_plans:view'],
  },
  {
    id: 'communications',
    label: 'Communications',
    labelEs: 'Comunicaciones',
    icon: 'MessageSquare',
    path: '/communications',
    requiredPermissions: ['communications:view'],
    badge: 'messages',
  },
  {
    id: 'reports',
    label: 'Reports',
    labelEs: 'Reportes',
    icon: 'BarChart3',
    path: '/reports',
    requiredPermissions: ['reports:view'],
  },
  {
    id: 'audits',
    label: 'Audits & Compliance',
    labelEs: 'Auditorías y Cumplimiento',
    icon: 'Shield',
    path: '/audits',
    requiredPermissions: ['audits:view'],
    children: [
      {
        id: 'audit-review',
        label: 'Case Review Queue',
        labelEs: 'Cola de Revisión de Casos',
        icon: 'FileSearch',
        path: '/audits/review',
        requiredPermissions: ['audits:view'],
        badge: 'alerts',
      },
      {
        id: 'audit-compliance',
        label: 'Compliance Dashboard',
        labelEs: 'Panel de Cumplimiento',
        icon: 'CheckCircle',
        path: '/audits/compliance',
        requiredPermissions: ['audits:view'],
      },
    ],
  },
  {
    id: 'team',
    label: 'Team',
    labelEs: 'Equipo',
    icon: 'Users2',
    path: '/team',
    requiredPermissions: ['team:view'],
  },
  {
    id: 'programs',
    label: 'Programs',
    labelEs: 'Programas',
    icon: 'FolderKanban',
    path: '/programs',
    requiredPermissions: ['programs:view'],
  },
  {
    id: 'settings',
    label: 'Settings',
    labelEs: 'Configuración',
    icon: 'Settings',
    path: '/settings',
    requiredPermissions: ['settings:view'],
    children: [
      {
        id: 'member-fields',
        label: 'Member Field Configuration',
        labelEs: 'Configuración de Campos de Miembros',
        icon: 'FormInput',
        path: '/admin/member-fields',
        requiredPermissions: ['settings:edit'],
      },
      {
        id: 'organization-roles',
        label: 'Organization Roles',
        labelEs: 'Roles de Organización',
        icon: 'Shield',
        path: '/admin/organization-roles',
        requiredPermissions: ['settings:edit'],
      },
    ],
  },
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get role definition, handling both system roles and legacy roles
 */
export function getRoleDefinition(role: string): RoleDefinition | undefined {
  // First check if it's a valid system role
  if (isSystemRole(role)) {
    return ROLE_DEFINITIONS[role];
  }
  // Check if it's a legacy role
  if (role === 'promotora' || role === 'care_coordinator') {
    return LEGACY_ROLE_DEFINITIONS[role];
  }
  // Default to staff role definition for unknown roles
  return ROLE_DEFINITIONS.staff;
}

/**
 * Check if a role has a specific permission
 * Handles both system roles and legacy roles
 */
export function roleHasPermission(role: CBORoleType | AllRoleTypes | string, permission: Permission): boolean {
  const systemRole = toSystemRole(role);
  const roleDefinition = ROLE_DEFINITIONS[systemRole];
  return roleDefinition?.permissions.includes(permission) ?? false;
}

/**
 * Check if a role has any of the specified permissions
 */
export function roleHasAnyPermission(role: CBORoleType, permissions: Permission[]): boolean {
  return permissions.some(permission => roleHasPermission(role, permission));
}

/**
 * Check if a role has all of the specified permissions
 */
export function roleHasAllPermissions(role: CBORoleType, permissions: Permission[]): boolean {
  return permissions.every(permission => roleHasPermission(role, permission));
}

/**
 * Get navigation items filtered by role permissions
 */
export function getNavigationForRole(role: CBORoleType): NavItem[] {
  const roleDefinition = ROLE_DEFINITIONS[role];
  if (!roleDefinition) return [];

  const filterNavItems = (items: NavItem[]): NavItem[] => {
    return items
      .filter(item => {
        if (item.requiredPermissions.length === 0) return true;
        return roleHasAnyPermission(role, item.requiredPermissions);
      })
      .map(item => ({
        ...item,
        children: item.children ? filterNavItems(item.children) : undefined,
      }))
      .filter(item => !item.children || item.children.length > 0);
  };

  return filterNavItems(NAVIGATION_ITEMS);
}

/**
 * Get the default dashboard path for a role
 */
export function getDefaultDashboardForRole(role: CBORoleType): string {
  return ROLE_DEFINITIONS[role]?.defaultDashboard ?? '/dashboard';
}

/**
 * Get role display name (localized)
 */
export function getRoleDisplayName(role: CBORoleType, locale: string = 'en'): string {
  const definition = ROLE_DEFINITIONS[role];
  if (!definition) return role;
  return locale === 'es' ? definition.displayNameEs : definition.displayName;
}

/**
 * Get role description (localized)
 */
export function getRoleDescription(role: CBORoleType, locale: string = 'en'): string {
  const definition = ROLE_DEFINITIONS[role];
  if (!definition) return '';
  return locale === 'es' ? definition.descriptionEs : definition.description;
}

/**
 * Check if role should see simplified UI
 */
export function shouldUseSimplifiedUI(role: CBORoleType): boolean {
  return ROLE_DEFINITIONS[role]?.uiSimplificationLevel === 'simplified';
}

/**
 * Check if role should see advanced features
 */
export function shouldShowAdvancedFeatures(role: CBORoleType): boolean {
  return ROLE_DEFINITIONS[role]?.showAdvancedFeatures ?? false;
}

/**
 * Get roles that a given role can manage
 */
export function getManageableRoles(role: CBORoleType): CBORoleType[] {
  return ROLE_DEFINITIONS[role]?.canManageRoles ?? [];
}

/**
 * User organization role type (from user_management system)
 * These are the roles assigned to users in the organization
 */
export type UserOrgRole = 'super_admin' | 'org_admin' | 'ops_admin' | 'manager' | 'staff' | 'member' | 'viewer';

/**
 * Mapping between user_org_role and CBORoleType
 * This links the user management roles to the CBO permission system
 */
export const USER_ORG_ROLE_TO_CBO_ROLE: Record<UserOrgRole, CBORoleType> = {
  'super_admin': 'super_admin',
  'org_admin': 'org_admin',
  'ops_admin': 'program_manager',  // Ops admin maps to program manager permissions
  'manager': 'supervisor',          // Manager maps to supervisor permissions
  'staff': 'staff',
  'member': 'viewer',               // Members have viewer-level permissions
  'viewer': 'viewer',
};

/**
 * Reverse mapping: CBORoleType to closest UserOrgRole
 * Used when assigning users to opportunities, etc.
 */
export const CBO_ROLE_TO_USER_ORG_ROLE: Record<CBORoleType, UserOrgRole> = {
  'super_admin': 'super_admin',
  'org_admin': 'org_admin',
  'program_manager': 'ops_admin',   // Program manager maps to ops_admin
  'supervisor': 'manager',          // Supervisor maps to manager
  'staff': 'staff',
  'viewer': 'viewer',
};

/**
 * Legacy role to UserOrgRole mapping for backward compatibility
 * @deprecated Use CBO_ROLE_TO_USER_ORG_ROLE with toSystemRole() instead
 */
export const LEGACY_CBO_ROLE_TO_USER_ORG_ROLE: Record<LegacyFieldWorkerRole, UserOrgRole> = {
  'promotora': 'staff',
  'care_coordinator': 'staff',
};

/**
 * Get the display information for a user_org_role
 */
export const USER_ORG_ROLE_INFO: Record<UserOrgRole, { label: string; labelEs: string; description: string; cboRole: CBORoleType }> = {
  'super_admin': {
    label: 'Super Administrator',
    labelEs: 'Super Administrador',
    description: 'Full system access across all organizations',
    cboRole: 'super_admin',
  },
  'org_admin': {
    label: 'Organization Administrator',
    labelEs: 'Administrador de Organización',
    description: 'Full access to organization settings, users, and data',
    cboRole: 'org_admin',
  },
  'ops_admin': {
    label: 'Operations Administrator',
    labelEs: 'Administrador de Operaciones',
    description: 'Internal platform operations access',
    cboRole: 'program_manager',
  },
  'manager': {
    label: 'Manager',
    labelEs: 'Gerente',
    description: 'Can manage programs, members, and workflows',
    cboRole: 'supervisor',
  },
  'staff': {
    label: 'Staff',
    labelEs: 'Personal',
    description: 'Can view and edit assigned members and enrollments',
    cboRole: 'staff',
  },
  'member': {
    label: 'Member',
    labelEs: 'Miembro',
    description: 'Access to member portal for care coordination services',
    cboRole: 'viewer',
  },
  'viewer': {
    label: 'Viewer',
    labelEs: 'Observador',
    description: 'Read-only access to organization data',
    cboRole: 'viewer',
  },
};

/**
 * Convert user_org_role to CBORoleType for permission checking
 */
export function mapUserOrgRoleToCBORole(userOrgRole: string): CBORoleType {
  return USER_ORG_ROLE_TO_CBO_ROLE[userOrgRole as UserOrgRole] ?? 'staff';
}

/**
 * Convert legacy role to new CBO role type
 * @deprecated Use mapUserOrgRoleToCBORole instead
 */
export function mapLegacyRole(legacyRole: string): CBORoleType {
  return mapUserOrgRoleToCBORole(legacyRole);
}
